// passwordPolicy.ts
//
// The account password's minimum length, in one place, because it was in three
// and they disagreed (takenote_decisions.md 14.2, Phase 10.5).
//
// ---------------------------------------------------------------------------
// WHAT WAS WRONG, AND WHICH NUMBER WON.
//
// PasswordResetPage enforced 12 - a check in its handler plus `minLength` on
// both boxes - while `supabase/config.toml` set `minimum_password_length = 6`,
// and SignupPage enforced nothing at all. Three surfaces, three answers, for
// one secret.
//
// THE SERVER'S VALUE IS THE ONE THAT BINDS, exactly as 9.2's ciphertext cap
// binds over the UI's character cap: a client check is a courtesy, and anything
// that talks to `/auth/v1/` directly skips it. That is an argument about where
// the truth lives, not about which number to pick - and picking the number is
// what the requirements' "Auth - Password Policy" row already did:
//
//     "Password must meet defined policy (>=12 chars ...)"
//     "Supabase configuration and client validation are aligned so the server
//      rejects weak passwords even if client validation is bypassed."
//
// So the page was right and the server was wrong. `config.toml` now reads
// `minimum_password_length = 12` and this module is what the pages import, so
// the next disagreement has to be introduced deliberately rather than by
// editing one of three copies.
//
// Measured against the running GoTrue (v2.191.0) rather than assumed - at the
// old setting of 6, through a live PASSWORD_RECOVERY session:
//
//     5 chars    422 weak_password  "Password should be at least 6 characters."
//     6 chars    200
//    11 chars    200
//
// THE ADMIN API IGNORES THIS ENTIRELY. Measured: `POST /auth/v1/admin/users`
// created accounts with 3- and 5-character passwords while the minimum was 6.
// That is why raising it does not disturb `pnpm e2e`, which creates every
// account it uses through the admin API.
//
// THE REST OF THE ROW, DECIDED IN PHASE 12.5 (14.2) - AND REVISED THE SAME DAY.
// The row also asked for "letters + digits" and "block trivial patterns".
//
//   - Character types: ADOPTED, stronger than the row asked, and ENFORCED BY
//     THE SERVER. At least 12 characters AND a lower-case letter, a capital, a
//     number and a symbol: `password_requirements =
//     "lower_upper_letters_digits_symbols"` in config.toml, which GoTrue checks
//     on signup and on every password change, whoever calls it. It was first
//     left out (a rule that refuses "correct horse battery staple" and passes
//     "Password1234!"); the product owner decided otherwise, and the estimator
//     below covers the second half of that objection.
//   - Trivial patterns: the strength estimator on Sign up and Reset password
//     (passwordStrength.ts), which refuses below a zxcvbn score of 3 - so
//     "Password1234!" meets the character rule and is still refused. GoTrue has
//     nothing equivalent, so that half is a client-side courtesy and says so.
// ---------------------------------------------------------------------------

/**
 * Minimum account-password length.
 *
 * MUST equal `minimum_password_length` in `supabase/config.toml`. `pnpm e2e`
 * asserts the pair against each other by reading both files, so a change to one
 * fails the suite rather than drifting.
 *
 * This is the ACCOUNT password - the one Supabase Auth holds, used for login
 * and for the re-authentication that gates account deletion (9.5). It is not
 * the passphrase that protects the keys on disk; that is
 * `MIN_PASSPHRASE_LENGTH` in the extension, and the two are separate secrets
 * with separate blast radii (9.5). They happen to share a number today, which
 * is a coincidence rather than a link. The passphrase's rules (6.1) are not
 * these.
 */
export const MIN_PASSWORD_LENGTH = 12;

/**
 * The character types the server requires. MUST equal `password_requirements`
 * in `supabase/config.toml`; `pnpm e2e` reads both files and fails if they
 * differ, and asserts the server refuses a password missing a type.
 */
export const SERVER_PASSWORD_REQUIREMENTS = "lower_upper_letters_digits_symbols";

/**
 * The sets behind that name, as the Supabase CLI hands them to GoTrue - a
 * password needs at least one character from each. ASCII only: an accented
 * letter is not a lower-case letter here, and a space or a £ is not a symbol,
 * because GoTrue does not count them. The symbol set was read from the CLI and
 * then checked against the running server (`pnpm e2e`), not assumed.
 */
export const PASSWORD_CHARACTER_SETS = [
  { id: "lower", chars: "abcdefghijklmnopqrstuvwxyz" },
  { id: "upper", chars: "ABCDEFGHIJKLMNOPQRSTUVWXYZ" },
  { id: "digit", chars: "0123456789" },
  { id: "symbol", chars: "!@#$%^&*()_+-=[]{};'\\:\"|<>?,./`~" },
] as const;

export type PasswordRule = { id: "length" | "case" | "digit" | "symbol"; label: string; met: boolean };

/**
 * Each rule, and whether this password meets it - for the checklist under the
 * box, which shows them as two rows of two:
 *
 *     At least 12 characters     Upper and lower case
 *     At least 1 number          At least 1 symbol, like ! # - ?
 *
 * Upper and lower case are ONE item, met only when both are there. The server
 * still counts them as two sets; on the page they read as one rule, and a
 * password with only one of them leaves the item unticked, which says what is
 * missing.
 */
export function passwordRules(password: string): PasswordRule[] {
  const has = (id: (typeof PASSWORD_CHARACTER_SETS)[number]["id"]) => {
    const chars = PASSWORD_CHARACTER_SETS.find((set) => set.id === id)!.chars;
    return [...password].some((ch) => chars.includes(ch));
  };
  return [
    { id: "length", label: `At least ${MIN_PASSWORD_LENGTH} characters`, met: password.length >= MIN_PASSWORD_LENGTH },
    { id: "case", label: "Upper and lower case", met: has("upper") && has("lower") },
    { id: "digit", label: "At least 1 number", met: has("digit") },
    { id: "symbol", label: "At least 1 symbol, like ! # - ?", met: has("symbol") },
  ];
}

/**
 * Is this acceptable as an account password, by the rules the server enforces?
 *
 * Exactly the server's rules and no more, so an honest mistake is caught
 * before a round trip and nothing the server would accept is refused here. The
 * strength estimator is a separate, stricter check the pages add.
 */
export function passwordMeetsPolicy(password: string): boolean {
  return passwordRules(password).every((rule) => rule.met);
}
